import React, { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Separator } from "../ui/separator";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  ArrowLeft,
  Mail,
  Phone,
  Building2,
  Edit,
  Loader2,
  Calendar,
  Award,
  Briefcase,
  DollarSign,
  TrendingUp,
  Eye,
  EyeOff,
  MapPin,
  CreditCard,
  User,
  Users,
  ChevronDown,
  Plus,
  Star,
  Trash2,
  Search,
  Pencil,
  Check,
  AlertCircle,
} from "lucide-react";
import { employeeApi, departmentApi, skillApi } from "../../services/api";
import { useToast } from "../../hooks/use-toast";
import { useManagerViewPermission } from "../../hooks/useManagerViewPermission";

// Organization Chart Component
interface OrgChartProps {
  employee: any;
  allEmployees: any[];
  departments: any[];
  getDepartmentName: (id: string) => string;
  getFullName: (firstName: string, lastName?: string) => string;
  getInitials: (firstName: string, lastName?: string) => string;
}

const OrgChartNode: React.FC<{
  emp: any;
  isCurrentEmployee: boolean;
  getFullName: (firstName: string, lastName?: string) => string;
  getInitials: (firstName: string, lastName?: string) => string;
  onClick?: () => void;
}> = ({ emp, isCurrentEmployee, getFullName, getInitials, onClick }) => (
  <div
    className={`flex flex-col items-center p-4 rounded-lg border-2 transition-all cursor-pointer hover:shadow-md min-w-[160px] ${
      isCurrentEmployee
        ? "border-primary bg-primary/5 shadow-md"
        : "border-gray-200 bg-white hover:border-gray-300"
    }`}
    onClick={onClick}
  >
    <Avatar
      className={`h-12 w-12 ${isCurrentEmployee ? "ring-2 ring-primary ring-offset-2" : ""}`}
    >
      <AvatarImage src={emp.avatar_url} />
      <AvatarFallback
        className={isCurrentEmployee ? "bg-primary text-white" : "bg-gray-100"}
      >
        {getInitials(emp.first_name, emp.last_name)}
      </AvatarFallback>
    </Avatar>
    <p
      className={`mt-2 font-medium text-sm text-center ${isCurrentEmployee ? "text-primary" : "text-gray-900"}`}
    >
      {getFullName(emp.first_name, emp.last_name)}
    </p>
    <p className="text-xs text-gray-500 text-center">
      {emp.job_title || "Employee"}
    </p>
    {isCurrentEmployee && (
      <Badge variant="default" className="mt-2 text-xs">
        You
      </Badge>
    )}
  </div>
);

const OrgChart: React.FC<OrgChartProps> = ({
  employee,
  allEmployees,
  departments,
  getDepartmentName,
  getFullName,
  getInitials,
}) => {
  const navigate = useNavigate();

  // Get current department
  const currentDepartment = departments.find(
    (d: any) => d.id === employee?.department_id,
  );

  // Get parent department (root)
  const parentDepartment = currentDepartment?.parent_id
    ? departments.find((d: any) => d.id === currentDepartment.parent_id)
    : null;

  // Get root department (if there's a grandparent, find it)
  const rootDepartment = parentDepartment?.parent_id
    ? departments.find((d: any) => d.id === parentDepartment.parent_id)
    : parentDepartment;

  // Get sibling departments (same parent as current department)
  const siblingDepartments = departments.filter(
    (d: any) =>
      d.parent_id === currentDepartment?.parent_id &&
      d.id !== currentDepartment?.id,
  );

  // Get employees by department
  const getEmployeesByDepartment = (deptId: string) => {
    return allEmployees.filter((e: any) => e.department_id === deptId);
  };

  // Get department manager
  const getDepartmentManager = (deptId: string) => {
    const dept = departments.find((d: any) => d.id === deptId);
    if (dept?.manager_id) {
      return allEmployees.find((e: any) => e.id === dept.manager_id);
    }
    // Fallback: find by title
    const deptEmployees = getEmployeesByDepartment(deptId);
    const managerTitles = [
      "manager",
      "director",
      "lead",
      "head",
      "chief",
      "vp",
      "president",
    ];
    return deptEmployees.find((e: any) =>
      managerTitles.some((title) => e.job_title?.toLowerCase().includes(title)),
    );
  };

  // Get employees in the same department (excluding manager and current employee)
  const departmentEmployees = useMemo(() => {
    if (!employee?.department_id) return [];
    const manager = getDepartmentManager(employee.department_id);
    return allEmployees.filter(
      (e: any) =>
        e.department_id === employee.department_id &&
        e.id !== employee.id &&
        e.id !== manager?.id,
    );
  }, [allEmployees, employee, departments]);

  // Current department manager
  const currentManager = getDepartmentManager(employee?.department_id);

  // Parent department manager
  const parentManager = parentDepartment
    ? getDepartmentManager(parentDepartment.id)
    : null;

  if (!employee) return null;

  // Department Node Component
  const DepartmentNode: React.FC<{
    dept: any;
    manager: any;
    employeeCount: number;
    isCurrentDept?: boolean;
    isDotted?: boolean;
  }> = ({ dept, manager, employeeCount, isCurrentDept, isDotted }) => (
    <div
      className={`flex flex-col items-center p-4 rounded-lg border-2 min-w-[200px] ${
        isCurrentDept
          ? "border-primary bg-primary/5"
          : "border-gray-200 bg-gray-50"
      }`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Building2
          className={`h-4 w-4 ${isCurrentDept ? "text-primary" : "text-gray-500"}`}
        />
        <span
          className={`font-semibold text-sm ${isCurrentDept ? "text-primary" : "text-gray-700"}`}
        >
          {dept?.name || "General Department"}
        </span>
      </div>
      {manager && (
        <div
          className="flex items-center gap-2 cursor-pointer hover:bg-white/50 rounded px-2 py-1"
          onClick={() => navigate(`/employees/${manager.id}`)}
        >
          <Avatar className="h-8 w-8">
            <AvatarImage src={manager.avatar_url} />
            <AvatarFallback className="text-xs bg-gray-200">
              {getInitials(manager.first_name, manager.last_name)}
            </AvatarFallback>
          </Avatar>
          <div className="text-left">
            <p className="text-xs font-medium">
              {getFullName(manager.first_name, manager.last_name)}
            </p>
            <p className="text-xs text-gray-500">
              {dept?.manager_id ? "Manager" : manager.job_title}
            </p>
          </div>
        </div>
      )}
      <Badge variant="secondary" className="mt-2 text-xs">
        {employeeCount} {employeeCount === 1 ? "employee" : "employees"}
      </Badge>
    </div>
  );

  return (
    <div className="py-4 overflow-x-auto">
      <div className="min-w-[600px] flex flex-col items-center">
        {/* Root/Parent Department Level */}
        {(rootDepartment || parentDepartment) && (
          <>
            <DepartmentNode
              dept={rootDepartment || parentDepartment}
              manager={
                rootDepartment
                  ? getDepartmentManager(rootDepartment.id)
                  : parentManager
              }
              employeeCount={
                getEmployeesByDepartment(
                  (rootDepartment || parentDepartment).id,
                ).length
              }
              isCurrentDept={false}
            />

            {/* Dotted connector to sub-departments */}
            <div className="w-0.5 h-8 border-l-2 border-dashed border-gray-400"></div>

            {/* Horizontal dotted line for multiple sub-departments */}
            {siblingDepartments.length > 0 && (
              <div className="relative w-full flex justify-center">
                <div
                  className="absolute top-0 h-0.5 border-t-2 border-dashed border-gray-400"
                  style={{
                    width: `${(siblingDepartments.length + 1) * 220}px`,
                  }}
                ></div>
              </div>
            )}
          </>
        )}

        {/* Current Department and Siblings */}
        {parentDepartment && siblingDepartments.length > 0 ? (
          <div className="flex gap-8 mt-8">
            {/* Sibling Departments */}
            {siblingDepartments.map((sibDept: any) => (
              <div key={sibDept.id} className="flex flex-col items-center">
                <div className="w-0.5 h-4 border-l-2 border-dashed border-gray-400 -mt-4 mb-2"></div>
                <DepartmentNode
                  dept={sibDept}
                  manager={getDepartmentManager(sibDept.id)}
                  employeeCount={getEmployeesByDepartment(sibDept.id).length}
                  isCurrentDept={false}
                  isDotted={true}
                />
              </div>
            ))}

            {/* Current Department */}
            <div className="flex flex-col items-center">
              <div className="w-0.5 h-4 border-l-2 border-dashed border-gray-400 -mt-4 mb-2"></div>
              <DepartmentNode
                dept={currentDepartment}
                manager={currentManager}
                employeeCount={
                  getEmployeesByDepartment(currentDepartment?.id).length
                }
                isCurrentDept={true}
              />
            </div>
          </div>
        ) : (
          /* Just current department if no hierarchy */
          !parentDepartment && (
            <div className="flex flex-col items-center">
              <DepartmentNode
                dept={currentDepartment}
                manager={currentManager}
                employeeCount={
                  getEmployeesByDepartment(currentDepartment?.id).length
                }
                isCurrentDept={true}
              />
            </div>
          )
        )}

        {/* Solid connector to employees */}
        <div className="w-0.5 h-8 bg-gray-300 mt-4"></div>

        {/* Current Employee (highlighted) */}
        <div className="relative">
          <OrgChartNode
            emp={employee}
            isCurrentEmployee={true}
            getFullName={getFullName}
            getInitials={getInitials}
          />
        </div>

        {/* Team Members / Peers */}
        {departmentEmployees.length > 0 && (
          <>
            <div className="w-0.5 h-8 bg-gray-300"></div>
            {/* Horizontal connector */}
            <div className="relative w-full flex justify-center">
              <div
                className="absolute top-0 h-0.5 bg-gray-300"
                style={{
                  width: `${Math.min(departmentEmployees.length, 4) * 180}px`,
                }}
              ></div>
            </div>
            <div className="flex flex-wrap justify-center gap-4 mt-8">
              {departmentEmployees.slice(0, 6).map((member: any) => (
                <div key={member.id} className="flex flex-col items-center">
                  <div className="w-0.5 h-4 bg-gray-300 -mt-4 mb-2"></div>
                  <OrgChartNode
                    emp={member}
                    isCurrentEmployee={false}
                    getFullName={getFullName}
                    getInitials={getInitials}
                    onClick={() => navigate(`/employees/${member.id}`)}
                  />
                </div>
              ))}
            </div>
          </>
        )}

        {/* No team members message */}
        {departmentEmployees.length === 0 && (
          <div className="mt-8 text-center text-gray-500">
            <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No other team members in this department</p>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="mt-12 pt-6 border-t">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded border-2 border-primary bg-primary/10"></div>
            <span>Current Department</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded border-2 border-gray-200 bg-gray-50"></div>
            <span>Other Departments</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 border-t-2 border-dashed border-gray-400"></div>
            <span>Reports To</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 border-t-2 border-gray-300"></div>
            <span>Team Members</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Employee Skills Section Component
const EmployeeSkillsSection: React.FC<{ employeeId: string }> = ({
  employeeId,
}) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isAddSkillOpen, setIsAddSkillOpen] = useState(false);
  const [selectedSkillIds, setSelectedSkillIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("addSkill") === "true") {
      setIsAddSkillOpen(true);
    }
  }, [location.search]);

  // Editing state: which skill is being edited + its local proficiency value
  const [editingSkillId, setEditingSkillId] = useState<string | null>(null);
  const [editProficiency, setEditProficiency] = useState<number>(5);

  // Fetch employee skills
  const { data: employeeSkills = [], isLoading: skillsLoading } = useQuery({
    queryKey: ["employeeSkills", employeeId],
    queryFn: () => skillApi.employeeSkills.getAll({ employee_id: employeeId }),
  });

  // Fetch all available skills from library
  const { data: allSkills = [] } = useQuery({
    queryKey: ["skills"],
    queryFn: () => skillApi.skills.getAll({}),
  });

  // Fetch skill types (categories)
  const { data: skillTypes = [] } = useQuery({
    queryKey: ["skillTypes"],
    queryFn: () => skillApi.types.getAll(),
  });

  // Bulk add skills mutation
  const bulkAddSkillMutation = useMutation({
    mutationFn: (data: any[]) => skillApi.employeeSkills.bulkCreate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["employeeSkills", employeeId],
      });
      setIsAddSkillOpen(false);
      setSelectedSkillIds(new Set());
      setSearchQuery("");
      toast({ title: "Skills added successfully" });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update skill proficiency mutation
  const updateSkillMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      skillApi.employeeSkills.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["employeeSkills", employeeId],
      });
      setEditingSkillId(null);
      toast({ title: "Skill level updated successfully" });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete skill mutation
  const deleteSkillMutation = useMutation({
    mutationFn: (id: string) => skillApi.employeeSkills.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["employeeSkills", employeeId],
      });
      toast({ title: "Skill removed successfully" });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleAddSkills = () => {
    if (selectedSkillIds.size === 0) {
      toast({
        title: "Error",
        description: "Please select at least one skill",
        variant: "destructive",
      });
      return;
    }
    const skillsToAdd = Array.from(selectedSkillIds).map((skillId) => ({
      employee_id: employeeId,
      skill_id: skillId,
      proficiency_level: 5,
    }));
    bulkAddSkillMutation.mutate(skillsToAdd);
  };

  const handleDeleteSkill = (skillId: string, skillName: string) => {
    if (
      window.confirm(`Are you sure you want to remove "${skillName}" skill?`)
    ) {
      deleteSkillMutation.mutate(skillId);
    }
  };

  // Start editing a skill — set local state from current value
  const startEditing = (empSkill: any) => {
    setEditingSkillId(empSkill.id);
    setEditProficiency(empSkill.proficiency_level || 5);
  };

  // Cancel editing — reset state
  const cancelEditing = () => {
    setEditingSkillId(null);
  };

  // Save the edited proficiency to the backend
  const saveEditing = (empSkillId: string) => {
    updateSkillMutation.mutate({
      id: empSkillId,
      data: { proficiency_level: editProficiency },
    });
  };

  // Handle number input change with clamping to 1-10
  const handleProficiencyInputChange = (value: string) => {
    const num = parseInt(value, 10);
    if (isNaN(num)) return;
    setEditProficiency(Math.max(1, Math.min(10, num)));
  };

  // Handle progress bar click to set proficiency
  const handleProgressBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const level = Math.max(1, Math.min(10, Math.round(percentage * 10)));
    setEditProficiency(level);
  };

  const getSkillName = (skillId: string) => {
    const skill = allSkills.find((s: any) => s.id === skillId);
    return skill?.name || "Unknown Skill";
  };

  const getRatingColor = (rating: number) => {
    if (rating >= 8) return "bg-green-500";
    if (rating >= 5) return "bg-yellow-500";
    if (rating >= 3) return "bg-orange-500";
    return "bg-red-500";
  };

  const getProficiencyLabel = (level: number) => {
    if (level <= 2) return "Beginner";
    if (level <= 4) return "Elementary";
    if (level <= 6) return "Intermediate";
    if (level <= 8) return "Advanced";
    return "Expert";
  };

  // Get skills not already assigned to employee
  const availableSkills = allSkills.filter(
    (s: any) => !employeeSkills.some((es: any) => es.skill_id === s.id),
  );

  // Filter available skills by search query
  const filteredAvailableSkills = searchQuery
    ? availableSkills.filter((s: any) =>
        s.name.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : availableSkills;

  // Group filtered available skills by skill type
  const groupedSkills = useMemo(() => {
    const groups: { type: any; skills: any[] }[] = [];
    skillTypes.forEach((type: any) => {
      const typeSkills = filteredAvailableSkills.filter(
        (s: any) => s.skill_type_id === type.id,
      );
      if (typeSkills.length > 0) {
        groups.push({ type, skills: typeSkills });
      }
    });
    // Also group skills without a type
    const ungrouped = filteredAvailableSkills.filter(
      (s: any) => !skillTypes.some((t: any) => t.id === s.skill_type_id),
    );
    if (ungrouped.length > 0) {
      groups.push({ type: { id: "__uncategorized", name: "Other" }, skills: ungrouped });
    }
    return groups;
  }, [skillTypes, filteredAvailableSkills]);

  // Toggle individual skill selection
  const toggleSkill = (skillId: string) => {
    setSelectedSkillIds((prev) => {
      const next = new Set(prev);
      if (next.has(skillId)) {
        next.delete(skillId);
      } else {
        next.add(skillId);
      }
      return next;
    });
  };

  // Toggle all skills in a category
  const toggleCategory = (categorySkills: any[]) => {
    const categorySkillIds = categorySkills.map((s: any) => s.id);
    const allSelected = categorySkillIds.every((id: string) => selectedSkillIds.has(id));

    setSelectedSkillIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        categorySkillIds.forEach((id: string) => next.delete(id));
      } else {
        categorySkillIds.forEach((id: string) => next.add(id));
      }
      return next;
    });
  };

  // Check if all skills in a category are selected
  const isCategoryFullySelected = (categorySkills: any[]) => {
    return categorySkills.every((s: any) => selectedSkillIds.has(s.id));
  };

  // Check if some (but not all) skills in a category are selected
  const isCategoryPartiallySelected = (categorySkills: any[]) => {
    const someSelected = categorySkills.some((s: any) => selectedSkillIds.has(s.id));
    const allSelected = categorySkills.every((s: any) => selectedSkillIds.has(s.id));
    return someSelected && !allSelected;
  };

  if (skillsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <Card className="border-none shadow-lg bg-white dark:bg-slate-900 overflow-hidden">
        <CardHeader className="border-b border-slate-50 dark:border-slate-800/50 bg-slate-50/50 dark:bg-slate-800/30 flex flex-row items-center justify-between py-4 px-8">
          <div className="flex items-center gap-3">
            <Star className="h-5 w-5 text-blue-500" />
            <CardTitle className="text-lg font-bold text-[#143A62] dark:text-white tracking-tight">Skills</CardTitle>
          </div>
          <Button 
            onClick={() => setIsAddSkillOpen(true)} 
            className="h-8 px-4"
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Add Skill
          </Button>
        </CardHeader>
        <CardContent className="p-8">
          {employeeSkills.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="h-16 w-16 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center mb-4">
                <Star className="h-8 w-8 text-slate-300 dark:text-slate-600" />
              </div>
              <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">No skills added</p>
              <p className="text-xs text-slate-400 mt-1">Add skills from the library to build the profile.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {employeeSkills.map((empSkill: any) => {
                const isEditing = editingSkillId === empSkill.id;
                const currentLevel = isEditing ? editProficiency : (empSkill.proficiency_level || 5);
                const skillName = empSkill.skill_name || getSkillName(empSkill.skill_id);

                return (
                  <div
                    key={empSkill.id}
                    className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800 group hover:border-blue-200 dark:hover:border-blue-900/50 transition-all duration-300"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200 tracking-tight">{skillName}</h4>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {isEditing ? (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              onClick={() => saveEditing(empSkill.id)}
                              disabled={updateSkillMutation.isPending}
                            >
                              {updateSkillMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-slate-400"
                              onClick={cancelEditing}
                            >
                              <span className="text-xs font-bold">✕</span>
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-slate-400 hover:text-blue-600"
                              onClick={() => startEditing(empSkill)}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-slate-400 hover:text-red-500"
                              onClick={() => handleDeleteSkill(empSkill.id, skillName)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                          {getProficiencyLabel(currentLevel)}
                        </span>
                        {isEditing ? (
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              min={1}
                              max={10}
                              value={editProficiency}
                              onChange={(e) => handleProficiencyInputChange(e.target.value)}
                              className="w-12 h-6 text-center text-[10px] font-bold p-0 border-blue-200"
                            />
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">/ 10</span>
                          </div>
                        ) : (
                          <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest">
                            {currentLevel} <span className="text-slate-300">/ 10</span>
                          </span>
                        )}
                      </div>

                      <div
                        className={`w-full bg-slate-200 dark:bg-slate-700 rounded-full h-1.5 overflow-hidden ${isEditing ? "cursor-pointer" : ""}`}
                        onClick={isEditing ? handleProgressBarClick : undefined}
                      >
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${getRatingColor(currentLevel)}`}
                          style={{ width: `${currentLevel * 10}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Skill Dialog */}
      <Dialog open={isAddSkillOpen} onOpenChange={(open) => {
        setIsAddSkillOpen(open);
        if (!open) {
          setSelectedSkillIds(new Set());
          setSearchQuery("");
        }
      }}>
        <DialogContent className="max-w-2xl border-none shadow-2xl bg-white dark:bg-slate-900 p-0 overflow-hidden rounded-3xl">
          <DialogHeader className="p-8 pb-4">
            <div className="flex items-center gap-3 mb-1">
              <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                <Star className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-white uppercase tracking-tight">Add Skills</DialogTitle>
            </div>
            <DialogDescription className="text-slate-500 dark:text-slate-400 font-medium">
              Select technical competencies from the company library to add to this profile.
            </DialogDescription>
          </DialogHeader>

          <div className="px-8 space-y-6">
            {/* Search bar */}
            <div className="relative group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
              <Input
                placeholder="Search skills library..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-11 h-12 bg-slate-50 dark:bg-slate-800/50 border-slate-100 dark:border-slate-800 focus:border-blue-500 rounded-2xl transition-all"
              />
            </div>

            {/* Skills grouped by category with checkboxes */}
            <div className="max-h-[400px] overflow-y-auto space-y-4 pr-2 custom-scrollbar">
              {groupedSkills.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="h-16 w-16 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center mb-4">
                    <Search className="h-8 w-8 text-slate-300 dark:text-slate-600" />
                  </div>
                  <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">No matching skills</p>
                  <p className="text-xs text-slate-400 mt-1">Try a different search term or check the library.</p>
                </div>
              ) : (
                groupedSkills.map((group) => (
                  <div key={group.type.id} className="overflow-hidden rounded-2xl bg-slate-50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-800">
                    {/* Category header with select-all checkbox */}
                    <div className="flex items-center gap-3 px-5 py-4 bg-white/50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
                      <Checkbox
                        id={`category-${group.type.id}`}
                        checked={isCategoryFullySelected(group.skills)}
                        data-indeterminate={isCategoryPartiallySelected(group.skills)}
                        onCheckedChange={() => toggleCategory(group.skills)}
                        className="rounded-md border-slate-300 dark:border-slate-700 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                      />
                      <Label
                        htmlFor={`category-${group.type.id}`}
                        className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-widest cursor-pointer flex-1"
                      >
                        {group.type.name}
                        <span className="ml-2 text-[10px] text-slate-400 font-bold">
                          {group.skills.length} SKILLS
                        </span>
                      </Label>
                    </div>

                    {/* Skills list */}
                    <div className="p-2 grid grid-cols-1 md:grid-cols-2 gap-1">
                      {group.skills.map((skill: any) => (
                        <div
                          key={skill.id}
                          className={`flex items-center gap-3 px-3 py-2 rounded-xl transition-all cursor-pointer hover:bg-white dark:hover:bg-slate-800 shadow-sm border border-transparent ${selectedSkillIds.has(skill.id) ? 'bg-blue-50/50 dark:bg-blue-900/10 border-blue-100 dark:border-blue-900/30' : ''}`}
                          onClick={() => toggleSkill(skill.id)}
                        >
                          <Checkbox
                            id={`skill-${skill.id}`}
                            checked={selectedSkillIds.has(skill.id)}
                            onCheckedChange={() => toggleSkill(skill.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="rounded-md border-slate-300 dark:border-slate-700 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                          />
                          <Label
                            htmlFor={`skill-${skill.id}`}
                            className="text-xs font-bold text-slate-600 dark:text-slate-400 tracking-tight cursor-pointer flex-1"
                          >
                            {skill.name}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <DialogFooter className="p-8 mt-2 bg-slate-50/50 dark:bg-slate-900/50 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
            <div className="flex-1">
              {selectedSkillIds.size > 0 && (
                <div className="flex items-center gap-2">
                  <Badge className="bg-blue-600 hover:bg-blue-700 text-white border-none font-black text-[10px] px-2 py-0.5 rounded-full">
                    {selectedSkillIds.size}
                  </Badge>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Selected to add</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                onClick={() => setIsAddSkillOpen(false)}
                className="h-10 px-6"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddSkills}
                disabled={selectedSkillIds.size === 0 || bulkAddSkillMutation.isPending}
                className="h-10 px-8"
              >
                {bulkAddSkillMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                Confirm Selection
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const EmployeeDetailNew: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [showSalary, setShowSalary] = useState(false);
  const [showBankAccount, setShowBankAccount] = useState(false);

  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [isCheckingDeletion, setIsCheckingDeletion] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [blockedModalState, setBlockedModalState] = useState<{
    isOpen: boolean;
    dealNames: string[];
    activityNames: string[];
    assignments: string[];
    message: string;
  }>({
    isOpen: false,
    dealNames: [],
    activityNames: [],
    assignments: [],
    message: "",
  });
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);

  const { isUserView, canViewCompensation, canViewSalaryDetails, canEditEmployee } = useManagerViewPermission();

  const { data: employee, isLoading: employeeLoading } = useQuery({
    queryKey: ["employee", id],
    queryFn: () => employeeApi.getById(id!),
    enabled: !!id,
  });

  const handleDeleteProfileClick = async () => {
    if (!id) return;
    setIsCheckingDeletion(true);
    try {
      const checkRes = await employeeApi.checkDeletionAssignments(id);
      if (!checkRes.can_delete) {
        setBlockedModalState({
          isOpen: true,
          dealNames: checkRes.deal_names || [],
          activityNames: checkRes.activity_names || [],
          assignments: checkRes.assignments || [],
          message: checkRes.message || "This user cannot be deleted because they are assigned into active tasks.",
        });
      } else {
        setShowConfirmDeleteModal(true);
      }
    } catch (err: any) {
      console.error("Failed to check deletion assignments:", err);
      toast({
        title: "Error checking assignments",
        description: err?.message || "Failed to check if employee has assigned deals or activities.",
        variant: "destructive",
      });
    } finally {
      setIsCheckingDeletion(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!id) return;
    setIsDeleting(true);
    try {
      await employeeApi.delete(id);
      toast({
        title: "Profile Deleted",
        description: `${getFullName(employee?.first_name, employee?.last_name)} has been successfully removed and moved to the archive.`,
      });
      queryClient.invalidateQueries({ queryKey: ["employees"] });
      queryClient.invalidateQueries({ queryKey: ["employee", id] });
      setShowConfirmDeleteModal(false);
      navigate("/employees");
    } catch (err: any) {
      console.error("Failed to delete employee:", err);
      toast({
        title: "Deletion Failed",
        description: err?.message || "Could not delete employee profile. Please try again.",
        variant: "destructive",
      });
      if (err?.message && err.message.toLowerCase().includes("assigned into")) {
        setShowConfirmDeleteModal(false);
        setBlockedModalState({
          isOpen: true,
          dealNames: [],
          activityNames: [],
          assignments: [],
          message: err.message,
        });
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const { data: departments = [] } = useQuery({
    queryKey: ["departments"],
    queryFn: () => departmentApi.getAll(),
  });

  // Fetch all employees for org chart
  const { data: allEmployees = [] } = useQuery({
    queryKey: ["employees"],
    queryFn: () => employeeApi.getAll({}),
  });

  // Fetch employee skills for overview tab
  const { data: employeeSkills = [] } = useQuery({
    queryKey: ["employeeSkills", id],
    queryFn: () => skillApi.employeeSkills.getAll({ employee_id: id! }),
    enabled: !!id,
  });

  const getFullName = (firstName: string, lastName?: string): string => {
    return lastName ? `${firstName} ${lastName}` : firstName;
  };

  const getInitials = (firstName: string, lastName?: string): string => {
    if (!lastName) return firstName.charAt(0).toUpperCase();
    return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
  };

  const formatDate = (date: string | Date): string => {
    if (!date) return "N/A";
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(new Date(date));
  };

  const getTeamBadgeDetails = (team: any, index: number) => {
    let name = "";
    let category: string | null = null;
    let key = `team-${index}`;

    if (typeof team === "string") {
      name = team;
      key = `team-${team}`;
    } else if (team && typeof team === "object") {
      name = team.name || "";
      category = team.product_category ? String(team.product_category).trim() : null;
      key = team.id ? `team-${team.id}` : `team-${name}-${index}`;
    }

    const label = category ? `${name}(${category})` : name;
    return { label, key };
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status?.toUpperCase()) {
      case "ACTIVE":
        return "default";
      case "ONBOARDING":
        return "secondary";
      case "OFFBOARDING":
        return "destructive";
      case "INACTIVE":
        return "outline";
      default:
        return "default";
    }
  };

  const getDepartmentName = (deptId: string) => {
    const dept = departments.find((d: any) => d.id === deptId);
    return dept?.name || "Unknown Department";
  };

  // Calculate work period
  const workPeriod = useMemo(() => {
    if (!employee?.hire_date) return "N/A";
    const hireDate = new Date(employee.hire_date);
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - hireDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const years = Math.floor(diffDays / 365);
    const months = Math.floor((diffDays % 365) / 30);

    if (years > 0) {
      return `${years} year${years > 1 ? "s" : ""} ${months} month${months > 1 ? "s" : ""}`;
    }
    return `${months} month${months > 1 ? "s" : ""}`;
  }, [employee?.hire_date]);

  if (!id) {
    return <div>Employee not found</div>;
  }

  if (employeeLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading employee details...</span>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg">Employee not found</p>
        <Button onClick={() => navigate("/employees")} className="mt-4">
          Back to Employees
        </Button>
      </div>
    );
  }

  // Empty data - will be fetched from API when available
  const performanceHistory: {
    year: number;
    score: number;
    rating: string;
    completedGoals: number;
    totalGoals: number;
  }[] = [];
  const bonuses: {
    date: string;
    amount: number;
    type: string;
    reason: string;
  }[] = [];
  const promotions: {
    date: string;
    fromTitle: string;
    toTitle: string;
    department: string;
  }[] = [];

  const getManagerName = () => {
    if (employee?.manager_name) return employee.manager_name;
    if (employee?.manager_id && allEmployees.length > 0) {
      const manager = allEmployees.find((e: any) => e.id === employee.manager_id);
      if (manager) {
        if (manager.name) return manager.name;
        return getFullName(manager.first_name, manager.last_name);
      }
    }
    return null;
  };

  const currentManagerName = getManagerName();

  return (
    <div className="h-full overflow-y-auto space-y-6 p-6 custom-scrollbar">
      {/* Breadcrumbs */}
      <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
        <button
          onClick={() => navigate("/")}
          className="hover:text-primary transition-colors"
        >
          Home
        </button>
        <span>/</span>
        <button
          onClick={() => navigate("/employees")}
          className="hover:text-primary transition-colors"
        >
          Employees
        </button>
        <span>/</span>
        <span className="text-gray-900 dark:text-white font-medium">
          {getFullName(employee.first_name, employee.last_name)}
        </span>
      </nav>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/employees')}
            className="h-10 w-10 rounded-full border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 shadow-sm transition-all"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-[#143A62] dark:text-slate-100">Employee Details</h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[12px] font-bold text-slate-400">Directory</span>
              <span className="text-slate-300 dark:text-slate-600 text-xs">/</span>
              <span className="text-[12px] font-bold text-blue-500">{getFullName(employee.first_name, employee.last_name)}</span>
            </div>
          </div>
        </div>

        {!isUserView && canEditEmployee && (
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => navigate(`/employees/${id}/edit`)} 
              className="tracking-tight h-10 px-6 transition-all bg-[#143A62] hover:bg-[#1e4e82] text-white shadow-sm"
            >
              <Edit className="h-3.5 w-3.5 mr-2" />
              Edit Profile
            </Button>

            <Button
              variant="destructive"
              onClick={handleDeleteProfileClick}
              disabled={isCheckingDeletion || isDeleting}
              className="tracking-tight h-10 px-5 transition-all bg-red-600 hover:bg-red-700 text-white shadow-sm"
            >
              {isCheckingDeletion ? (
                <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5 mr-2" />
              )}
              Delete Profile
            </Button>
          </div>
        )}
      </div>

      {/* --- Profile Overview Card --- */}
      <Card className="border-none shadow-xl bg-white/70 backdrop-blur-md dark:bg-slate-900/70 overflow-hidden">
        <CardContent className="p-0">
          <div className="relative">
            {/* Background Header Decoration */}
            <div className="h-32 w-full bg-gradient-to-r from-blue-600/10 via-[#143A62]/10 to-teal-500/10 dark:from-blue-600/20 dark:via-[#143A62]/20 dark:to-teal-500/20 border-b border-white/20 dark:border-slate-800/20" />
            
            <div className="px-8 pb-8 flex flex-col md:flex-row items-end gap-6 -mt-16 relative z-10">
              <div className="p-1.5 bg-white dark:bg-slate-900 rounded-3xl shadow-2xl">
                <Avatar className="h-32 w-32 rounded-2xl border-4 border-white dark:border-slate-900">
                  <AvatarImage src={employee.avatar_url} />
                  <AvatarFallback className="bg-gradient-to-br from-blue-500 to-[#143A62] text-white text-4xl font-bold">
                    {getInitials(employee.first_name, employee.last_name)}
                  </AvatarFallback>
                </Avatar>
              </div>

              <div className="flex-1 pb-2">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h2 className="text-4xl font-bold text-slate-800 dark:text-white tracking-tight">
                    {getFullName(employee.first_name, employee.last_name)}
                  </h2>
                  <Badge className={`text-[11px] py-1 px-3 rounded-full font-bold border-none shadow-sm ${
                    employee.status?.toUpperCase() === 'ACTIVE' 
                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" 
                      : employee.status?.toUpperCase() === 'ONBOARDING'
                      ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                      : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400"
                  }`}>
                    {employee.status || 'Active'}
                  </Badge>
                </div>
                <div className="flex flex-wrap items-center gap-6 text-slate-500 dark:text-slate-400">
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-blue-500" />
                    <span className="text-sm font-medium">{employee.job_title || 'Unassigned Role'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-blue-500" />
                    <span className="text-sm font-medium">{employee.department_id ? getDepartmentName(employee.department_id) : 'General Department'}</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3 pb-2">
                <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm">
                  <Mail className="h-4 w-4 text-blue-500" />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{employee.work_email || 'No email provided'}</span>
                </div>
                <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm">
                  <Phone className="h-4 w-4 text-blue-500" />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{employee.work_phone || 'No phone provided'}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* --- Detail Tabs --- */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-white/50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 p-1 mb-8 w-fit shadow-sm backdrop-blur-sm">
          <TabsTrigger value="overview" className="px-6 py-2 text-sm font-bold data-[state=active]:bg-[#143A62] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600 transition-all rounded-md">
            Overview
          </TabsTrigger>
          <TabsTrigger value="personal" className="px-6 py-2 text-sm font-bold data-[state=active]:bg-[#143A62] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600 transition-all rounded-md">
            Personal
          </TabsTrigger>
          <TabsTrigger value="work" className="px-6 py-2 text-sm font-bold data-[state=active]:bg-[#143A62] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600 transition-all rounded-md">
            Work Info
          </TabsTrigger>
          <TabsTrigger value="performance" className="px-6 py-2 text-sm font-bold data-[state=active]:bg-[#143A62] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600 transition-all rounded-md">
            Performance
          </TabsTrigger>
          <TabsTrigger value="skills" className="px-6 py-2 text-sm font-bold data-[state=active]:bg-[#143A62] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600 transition-all rounded-md">
            Skills
          </TabsTrigger>
          {!isUserView && canViewCompensation && (
            <TabsTrigger value="compensation" className="px-6 py-2 text-sm font-bold data-[state=active]:bg-[#143A62] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600 transition-all rounded-md">
              Compensation
            </TabsTrigger>
          )}
          <TabsTrigger value="org-chart" className="px-6 py-2 text-sm font-bold data-[state=active]:bg-[#143A62] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600 transition-all rounded-md">
            Org Chart
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-0 space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Employment Information Card */}
            <Card className="border-none shadow-lg bg-white dark:bg-slate-900 overflow-hidden">
              <CardHeader className="border-b border-slate-50 dark:border-slate-800/50 bg-slate-50/50 dark:bg-slate-800/30">
                <div className="flex items-center gap-3">
                  <Briefcase className="h-5 w-5 text-blue-500" />
                  <CardTitle className="text-lg font-bold text-[#143A62] dark:text-white">Employment Information</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-12">
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Employee ID</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">#{employee.employee_number || employee.id.slice(0, 8).toUpperCase()}</p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Job Title</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{employee.job_title || "N/A"}</p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Department</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{employee.department_id ? getDepartmentName(employee.department_id) : "N/A"}</p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Employment Type</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{employee.employment_type || "Full-time"}</p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Join Date</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{formatDate(employee.hire_date)}</p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Work Period</p>
                    <p className="text-sm font-bold text-blue-600 dark:text-blue-400">{workPeriod}</p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Status</p>
                    <Badge className={`text-[10px] font-bold uppercase tracking-tight py-0.5 px-2.5 rounded-full border-none w-fit ${
                      employee.status?.toUpperCase() === 'ACTIVE' 
                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" 
                        : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400"
                    }`}>
                      {employee.status || 'Active'}
                    </Badge>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">System Role</p>
                    <Badge variant="secondary" className="capitalize text-[11px] font-bold py-0 h-5 w-fit">
                      {employee.system_role || employee.role || "User"}
                    </Badge>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Reports to (Manager)</p>
                    <div className="flex items-center gap-2">
                      {currentManagerName ? (
                        <Badge variant="outline" className="bg-blue-50/50 text-blue-700 border-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800 text-[11px] font-bold">
                          {currentManagerName}
                        </Badge>
                      ) : (
                        <p className="text-sm font-bold text-slate-800 dark:text-slate-200">
                          {employee.manager_id ? "Assigned (Loading...)" : "N/A"}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1.5 md:col-span-2">
                    <p className="text-xs font-bold text-slate-400">Teams Associated</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {employee.teams && employee.teams.length > 0 ? (
                        employee.teams.map((team: any, index: number) => {
                          const { label, key } = getTeamBadgeDetails(team, index);
                          return (
                            <Badge
                              key={key}
                              variant="outline"
                              className="text-[10px] font-bold bg-emerald-50/50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800"
                            >
                              {label}
                            </Badge>
                          );
                        })
                      ) : (
                        <p className="text-sm font-bold text-slate-800 dark:text-slate-200">N/A</p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Info Card */}
            <Card className="border-none shadow-lg bg-white dark:bg-slate-900 overflow-hidden">
              <CardHeader className="border-b border-slate-50 dark:border-slate-800/50 bg-slate-50/50 dark:bg-slate-800/30">
                <div className="flex items-center gap-3">
                  <User className="h-5 w-5 text-blue-500" />
                  <CardTitle className="text-lg font-bold text-[#143A62] dark:text-white">Quick Info</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-12">
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Email</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{employee.work_email || employee.email}</p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Phone</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{employee.work_phone || employee.phone || "N/A"}</p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Date of Birth</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{employee.date_of_birth ? formatDate(employee.date_of_birth) : "N/A"}</p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Gender</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{employee.gender || "N/A"}</p>
                  </div>
                </div>

                {/* Top Skills Overview inline inside Quick Info */}
                {employeeSkills && employeeSkills.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-slate-100 dark:border-slate-800">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <Star className="h-5 w-5 text-blue-500" />
                        <h3 className="text-lg font-bold text-[#143A62] dark:text-white">Employee Skills</h3>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-[10px] h-6 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/30 font-bold uppercase tracking-tight"
                        onClick={() => setActiveTab("skills")}
                      >
                        View Details
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {employeeSkills.map((empSkill: any) => (
                        <div key={empSkill.id} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800/50 text-slate-700 dark:text-slate-300 border border-slate-100 dark:border-slate-800 transition-all hover:border-blue-200 hover:bg-blue-50/50 dark:hover:border-blue-900/50 dark:hover:bg-blue-900/20">
                          <span className="text-sm font-bold">{empSkill.skill_name || `Skill #${empSkill.skill_id}`}</span>
                          {empSkill.proficiency_level && (
                            <div className="flex bg-white dark:bg-slate-900 rounded-md h-5 min-w-[20px] px-1.5 border border-slate-200 dark:border-slate-700 items-center justify-center shadow-sm" title={`Proficiency Level: ${empSkill.proficiency_level}/5`}>
                              <span className="text-xs font-black text-blue-600 dark:text-blue-400">{empSkill.proficiency_level}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Personal Information Tab */}
        <TabsContent value="personal" className="mt-0 space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <Card className="border-none shadow-lg bg-white dark:bg-slate-900 overflow-hidden">
            <CardHeader className="border-b border-slate-50 dark:border-slate-800/50 bg-slate-50/50 dark:bg-slate-800/30">
              <div className="flex items-center gap-3">
                <User className="h-5 w-5 text-blue-500" />
                <CardTitle className="text-lg font-bold text-[#143A62] dark:text-white">Personal Information</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                <div className="space-y-6">
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Full Name</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{getFullName(employee.first_name, employee.last_name)}</p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Date of Birth</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{employee.date_of_birth ? formatDate(employee.date_of_birth) : "N/A"}</p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Gender</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{employee.gender || "N/A"}</p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Marital Status</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{employee.marital_status || "N/A"}</p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Nationality</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{employee.nationality || "N/A"}</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Personal Email</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{employee.email || "N/A"}</p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Personal Phone</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{employee.phone || "N/A"}</p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Identity Number</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{employee.identity_number || "N/A"}</p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Emergency Contact</p>
                    <div className="space-y-1">
                      <p className="text-sm font-bold text-slate-800 dark:text-slate-200">
                        {employee.emergency_contact_name || "N/A"}
                      </p>
                      {employee.emergency_contact_relationship && (
                        <p className="text-[11px] text-blue-600 dark:text-blue-400 font-bold uppercase tracking-tight">
                          {employee.emergency_contact_relationship}
                        </p>
                      )}
                      {employee.emergency_contact_phone && (
                        <p className="text-xs font-bold text-slate-500 dark:text-slate-400">
                          {employee.emergency_contact_phone}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Work Info Tab */}
        <TabsContent value="work" className="mt-0 space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <Card className="border-none shadow-lg bg-white dark:bg-slate-900 overflow-hidden">
            <CardHeader className="border-b border-slate-50 dark:border-slate-800/50 bg-slate-50/50 dark:bg-slate-800/30">
              <div className="flex items-center gap-3">
                <Briefcase className="h-5 w-5 text-blue-500" />
                <CardTitle className="text-lg font-bold text-[#143A62] dark:text-white">Work Information</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                <div className="space-y-6">
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Employee Number</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">#{employee.employee_number || employee.id.slice(0, 8).toUpperCase()}</p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Job Title</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{employee.job_title || "N/A"}</p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Department</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{employee.department_id ? getDepartmentName(employee.department_id) : "N/A"}</p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Employment Type</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{employee.employment_type || "Full-time"}</p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Join Date</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{formatDate(employee.hire_date)}</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Work Email</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{employee.work_email || employee.email}</p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Work Phone</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{employee.work_phone || "N/A"}</p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Office Location</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{employee.office_location || "N/A"}</p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Work Schedule</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">
                      {employee.work_schedule || "N/A"}
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Probation End Date</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">
                      {employee.probation_end_date ? formatDate(employee.probation_end_date) : "N/A"}
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Reports to (Manager)</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">
                      {currentManagerName || (employee.manager_id ? "Assigned (Loading...)" : "N/A")}
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-400">Teams Associated</p>
                    <div className="flex flex-wrap gap-1">
                      {employee.teams && employee.teams.length > 0 ? (
                        employee.teams.map((team: any, index: number) => {
                          const { label, key } = getTeamBadgeDetails(team, index);
                          return (
                            <Badge key={key} variant="outline" className="text-[10px] font-bold">
                              {label}
                            </Badge>
                          );
                        })
                      ) : (
                        <p className="text-sm font-bold text-slate-800 dark:text-slate-200">N/A</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {!isUserView && canViewSalaryDetails && (
                <div className="mt-12 pt-8 border-t border-slate-100 dark:border-slate-800">
                  <div className="max-w-lg mx-auto">
                    <div className="p-6 bg-slate-50 dark:bg-slate-800/50 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-sm">
                      <div className="flex items-center justify-between mb-8">
                        <div className="flex items-center gap-2.5">
                          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                            <DollarSign className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          </div>
                          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Compensation Details</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowSalary(!showSalary)}
                          className="h-8 px-3 text-[10px] font-bold uppercase tracking-tight hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-all"
                        >
                          {showSalary ? (
                            <>
                              <EyeOff className="h-3.5 w-3.5 mr-1.5" /> Hide
                            </>
                          ) : (
                            <>
                              <Eye className="h-3.5 w-3.5 mr-1.5" /> Show
                            </>
                          )}
                        </Button>
                      </div>
                      
                      {showSalary ? (
                        <div className="space-y-5">
                          <div className="flex justify-between items-center px-1">
                            <span className="text-xs font-bold text-slate-400">Base Salary</span>
                            <span className="text-sm font-bold text-slate-800 dark:text-slate-200">
                              ${employee.base_salary?.toLocaleString() || "80,000"}
                            </span>
                          </div>
                          <div className="flex justify-between items-center px-1">
                            <span className="text-xs font-bold text-slate-400">Allowances</span>
                            <span className="text-sm font-bold text-slate-800 dark:text-slate-200">
                              ${employee.allowances?.toLocaleString() || "5,000"}
                            </span>
                          </div>
                          <div className="flex justify-between items-center pt-6 px-1 border-t border-slate-200 dark:border-slate-700">
                            <span className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest">Total Package</span>
                            <span className="text-xl font-black text-[#143A62] dark:text-white">
                              $
                              {(
                                (employee.base_salary || 80000) +
                                (employee.allowances || 5000)
                              ).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center py-6">
                          <div className="flex gap-2">
                            {[...Array(6)].map((_, i) => (
                              <div key={i} className="w-2 h-2 rounded-full bg-slate-200 dark:bg-slate-700 animate-pulse" />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance" className="mt-0 space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <Card className="border-none shadow-lg bg-white dark:bg-slate-900 overflow-hidden">
            <CardHeader className="border-b border-slate-50 dark:border-slate-800/50 bg-slate-50/50 dark:bg-slate-800/30">
              <div className="flex items-center gap-3">
                <TrendingUp className="h-5 w-5 text-blue-500" />
                <CardTitle className="text-lg font-bold text-[#143A62] dark:text-white">Performance Overview</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-8">
              {performanceHistory.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="h-16 w-16 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center mb-4">
                    <TrendingUp className="h-8 w-8 text-slate-300 dark:text-slate-600" />
                  </div>
                  <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">No history recorded</p>
                  <p className="text-xs text-slate-400 mt-1">Performance metrics will appear here after the first review.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {performanceHistory.map((perf) => (
                    <div key={perf.year} className="p-6 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                      <div className="flex items-center justify-between mb-6">
                        <div>
                           <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Review Year</p>
                           <h3 className="text-2xl font-black text-[#143A62] dark:text-white">{perf.year}</h3>
                        </div>
                        <div className="text-right">
                           <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Rating</p>
                           <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-none font-bold uppercase tracking-widest text-[10px] px-3 py-1">
                             {perf.rating}
                           </Badge>
                        </div>
                      </div>

                      <div className="space-y-4">
                         <div className="flex justify-between items-end mb-1">
                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Goals Achievement</span>
                            <span className="text-sm font-bold text-slate-800 dark:text-slate-200">{perf.completedGoals} / {perf.totalGoals}</span>
                         </div>
                         <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden shadow-inner">
                            <div
                              className="bg-gradient-to-r from-blue-500 to-teal-400 h-full rounded-full transition-all duration-500"
                              style={{ width: `${(perf.completedGoals / perf.totalGoals) * 100}%` }}
                            />
                         </div>
                         <div className="flex justify-between items-center pt-2">
                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Score</span>
                            <span className="text-xl font-black text-blue-600">{perf.score} <span className="text-xs text-slate-400 font-bold">/ 5.0</span></span>
                         </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Skills Tab */}
        <TabsContent value="skills" className="space-y-6">
          <EmployeeSkillsSection employeeId={employee.id} />
        </TabsContent>

        {/* Compensation Tab */}
        {!isUserView && canViewCompensation && (
          <TabsContent value="compensation" className="mt-0 space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Bonuses Card */}
              <Card className="border-none shadow-lg bg-white dark:bg-slate-900 overflow-hidden">
                <CardHeader className="border-b border-slate-50 dark:border-slate-800/50 bg-slate-50/50 dark:bg-slate-800/30">
                  <div className="flex items-center gap-3">
                    <Award className="h-5 w-5 text-blue-500" />
                    <CardTitle className="text-lg font-bold text-[#143A62] dark:text-white  tracking-tight">Bonuses</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="p-8">
                  {bonuses.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-6 text-center">
                       <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">No records found</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {bonuses.map((bonus, idx) => (
                        <div key={idx} className="flex items-center justify-between p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                          <div>
                            <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{bonus.type}</p>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">{bonus.reason}</p>
                            <p className="text-[10px] text-slate-400 font-medium mt-0.5">{formatDate(bonus.date)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-black text-emerald-600">${bonus.amount.toLocaleString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Promotions Card */}
              <Card className="border-none shadow-lg bg-white dark:bg-slate-900 overflow-hidden">
                <CardHeader className="border-b border-slate-50 dark:border-slate-800/50 bg-slate-50/50 dark:bg-slate-800/30">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="h-5 w-5 text-blue-500" />
                    <CardTitle className="text-lg font-bold text-[#143A62] dark:text-white tracking-tight">Promotions</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="p-8">
                  {promotions.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-6 text-center">
                       <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">No records found</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {promotions.map((promo, idx) => (
                        <div key={idx} className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                          <div className="flex items-center justify-between mb-2">
                             <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-none font-bold uppercase tracking-widest text-[9px] px-2 py-0.5">Promotion</Badge>
                             <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">{formatDate(promo.date)}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">{promo.fromTitle}</span>
                            <ArrowLeft className="h-3 w-3 text-slate-300 rotate-180" />
                            <span className="text-sm font-bold text-blue-600">{promo.toTitle}</span>
                          </div>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-2">{promo.department}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Bank Details */}
            <Card className="border-none shadow-lg bg-white dark:bg-slate-900 overflow-hidden">
              <CardHeader className="border-b border-slate-50 dark:border-slate-800/50 bg-slate-50/50 dark:bg-slate-800/30">
                <div className="flex items-center gap-3">
                  <CreditCard className="h-5 w-5 text-blue-500" />
                  <CardTitle className="text-lg font-bold text-[#143A62] dark:text-white">Bank Details</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                  <div className="space-y-1.5">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Bank Name</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{employee.bank_name || "N/A"}</p>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium text-muted-foreground">
                        Account Number
                      </label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowBankAccount(!showBankAccount)}
                        className="h-6 px-2 text-xs"
                      >
                        {showBankAccount ? (
                          <EyeOff className="h-3 w-3 mr-1" />
                        ) : (
                          <Eye className="h-3 w-3 mr-1" />
                        )}
                        {showBankAccount ? "Hide" : "Show"}
                      </Button>
                    </div>
                    <p className="text-sm font-mono font-medium">
                      {employee.bank_account
                        ? showBankAccount 
                          ? employee.bank_account 
                          : `•••• •••• ${employee.bank_account.slice(-4)}`
                        : "N/A"}
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Routing Number</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{employee.routing_number || "N/A"}</p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Account Type</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">{employee.account_type || "N/A"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Org Chart Tab */}
        <TabsContent value="org-chart" className="mt-0 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <Card className="border-none shadow-lg bg-white dark:bg-slate-900 overflow-hidden">
            <CardHeader className="border-b border-slate-50 dark:border-slate-800/50 bg-slate-50/50 dark:bg-slate-800/30">
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-blue-500" />
                <CardTitle className="text-lg font-bold text-[#143A62] dark:text-white tracking-tight">Organization Structure</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-8">
              <OrgChart
                employee={employee}
                allEmployees={allEmployees}
                departments={departments}
                getDepartmentName={getDepartmentName}
                getFullName={getFullName}
                getInitials={getInitials}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* --- Blocked Deletion Modal (Assigned Deals / Activities) --- */}
      <Dialog
        open={blockedModalState.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            setBlockedModalState((prev) => ({ ...prev, isOpen: false }));
          }
        }}
      >
        <DialogContent className="max-w-lg p-0 overflow-hidden border-none shadow-2xl rounded-2xl bg-white dark:bg-slate-900">
          <div className="p-6 bg-gradient-to-br from-amber-500/10 via-red-500/5 to-transparent border-b border-amber-100 dark:border-amber-900/30">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-2xl bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 shadow-sm">
                <AlertCircle className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white">
                  Cannot Delete Employee Profile
                </DialogTitle>
                <DialogDescription className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  Active assignments must be reassigned before this user profile can be deleted.
                </DialogDescription>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <div className="p-4 rounded-xl bg-amber-50/80 dark:bg-amber-950/20 border border-amber-200/70 dark:border-amber-800/40">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-200 leading-relaxed">
                {blockedModalState.message || "This user cannot be deleted because they are assigned to active deals or activities."}
              </p>
            </div>

            {/* Structured details list if available */}
            {blockedModalState.dealNames.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  <Briefcase className="w-3.5 h-3.5 text-blue-500" />
                  Assigned Deals ({blockedModalState.dealNames.length})
                </div>
                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-2 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200/60 dark:border-slate-800">
                  {blockedModalState.dealNames.map((name, idx) => (
                    <Badge key={idx} variant="outline" className="bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700">
                      {name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {blockedModalState.activityNames.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  <Calendar className="w-3.5 h-3.5 text-purple-500" />
                  Assigned Activities ({blockedModalState.activityNames.length})
                </div>
                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-2 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200/60 dark:border-slate-800">
                  {blockedModalState.activityNames.map((name, idx) => (
                    <Badge key={idx} variant="outline" className="bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700">
                      {name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="p-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800 flex justify-end">
            <Button
              onClick={() => setBlockedModalState((prev) => ({ ...prev, isOpen: false }))}
              className="bg-[#143A62] hover:bg-[#1e4e82] text-white px-6 font-semibold shadow-sm"
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Safe Deletion Confirmation Modal --- */}
      <Dialog
        open={showConfirmDeleteModal}
        onOpenChange={(open) => {
          if (!isDeleting) {
            setShowConfirmDeleteModal(open);
          }
        }}
      >
        <DialogContent className="max-w-md p-0 overflow-hidden border-none shadow-2xl rounded-2xl bg-white dark:bg-slate-900">
          <div className="p-6 bg-gradient-to-br from-red-500/10 via-rose-500/5 to-transparent border-b border-red-100 dark:border-red-900/30">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-2xl bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 shadow-sm">
                <Trash2 className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white">
                  Delete Employee Profile
                </DialogTitle>
                <DialogDescription className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  This action will archive and remove the employee profile.
                </DialogDescription>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <p className="text-base text-slate-700 dark:text-slate-300 font-medium">
              Are you sure you want to delete this user?
            </p>

            <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 flex items-center gap-3">
              <Avatar className="h-10 w-10 border border-white dark:border-slate-700 shadow-sm">
                <AvatarImage src={employee?.avatar_url} />
                <AvatarFallback className="bg-gradient-to-br from-blue-500 to-[#143A62] text-white font-bold text-sm">
                  {getInitials(employee?.first_name || "", employee?.last_name)}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-slate-900 dark:text-white truncate">
                  {getFullName(employee?.first_name, employee?.last_name)}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                  {employee?.display_email || employee?.email || employee?.job_title || "Employee Profile"}
                </p>
              </div>
            </div>

            <p className="text-xs text-slate-500 dark:text-slate-400">
              The user profile data will be removed from active profiles, archived into the deleted records collection (<code className="text-[11px] font-mono text-slate-700 dark:text-slate-300">Delete_employee_profiles</code>), and removed from any assigned teams.
            </p>
          </div>

          <DialogFooter className="p-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800 flex items-center justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowConfirmDeleteModal(false)}
              disabled={isDeleting}
              className="border-slate-200 dark:border-slate-700"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold shadow-sm min-w-[90px]"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete Profile"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeDetailNew;
